import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";

function parseDrafts(raw) {
  if (Array.isArray(raw)) return raw.map((x) => String(x || "").trim()).filter(Boolean);
  if (!raw) return [];
  const t = String(raw).trim();
  if (t.startsWith("[")) {
    try {
      const d = JSON.parse(t);
      if (Array.isArray(d)) return d.map((x) => String(x || "").trim()).filter(Boolean);
    } catch {
      /* plain text */
    }
  }
  return t ? [t] : [];
}

function statusLabel(status) {
  switch (status) {
    case "pending":
      return "en attente";
    case "contacted":
      return "envoyé";
    case "invited":
      return "invité";
    case "visited":
      return "visité";
    case "error":
      return "erreur";
    default:
      return status || "";
  }
}

function statusClass(status) {
  if (status === "pending") return "border-amber-200 text-amber-700 bg-amber-50";
  if (status === "error") return "border-rouge/20 text-rouge bg-rouge/5";
  if (status === "contacted" || status === "invited" || status === "visited") {
    return "border-emerald-200 text-emerald-700 bg-emerald-50";
  }
  return "border-line text-mute bg-cream";
}

function formatWhen(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`);
    return d.toLocaleString("fr-FR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function SwitchRow({ title, description, meta, on, busy, onToggle }) {
  return (
    <div className="mb-3 p-3 bg-cream rounded-lg border border-line">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="type-label-accent mb-0.5">{title}</p>
          <p className="text-xs text-mute">{description}</p>
          {meta && <p className="text-xs text-ink/50 mt-1">{meta}</p>}
        </div>
        <button
          type="button"
          onClick={onToggle}
          disabled={busy}
          aria-pressed={on}
          className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full border transition-colors disabled:opacity-50 ${
            on ? "bg-rouge border-rouge" : "bg-ink/10 border-ink/10"
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
              on ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
      </div>
    </div>
  );
}

export default function SegmentCard({ segment, onUpdate, onDelete }) {
  const [togglingSend, setTogglingSend] = useState(false);
  const [togglingSearch, setTogglingSearch] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [showContacts, setShowContacts] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [searches, setSearches] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [draftById, setDraftById] = useState({});
  const [busyById, setBusyById] = useState({});
  const draftTimers = useRef({});

  const isDmMode = segment.mode === "dm";

  const flash = (payload, ms = 6000) => {
    setFeedback(payload);
    setTimeout(() => setFeedback(null), payload.ms || ms);
  };

  const setBusy = (contactId, value) => {
    setBusyById((prev) => ({ ...prev, [contactId]: value }));
  };

  const handleToggleSend = async () => {
    try {
      setTogglingSend(true);
      const data = await api.post(`/api/segments/${segment.id}/toggle`, {});
      onUpdate({ ...segment, is_active: data.is_active });
      flash({
        ok: true,
        text: data.is_active
          ? "Envoi auto activé — le moteur envoie dans la plage horaire"
          : "Envoi auto désactivé",
      });
    } catch (err) {
      flash({ ok: false, text: err.message || "Impossible de basculer l'envoi auto" });
    } finally {
      setTogglingSend(false);
    }
  };

  const handleToggleAutoSearch = async () => {
    const next = !Boolean(segment.auto_search);
    // Optimistic UI — switch flips instantly
    onUpdate({ ...segment, auto_search: next ? 1 : 0 });
    try {
      setTogglingSearch(true);
      const data = await api.post(`/api/segments/${segment.id}/toggle-auto-search`, {});
      const updated = data.segment || { ...segment, auto_search: data.auto_search };
      onUpdate({
        ...segment,
        ...updated,
        auto_search: data.auto_search ? 1 : 0,
        contacts_count: updated.contacts_count ?? segment.contacts_count,
        pending_count: updated.pending_count ?? segment.pending_count,
        last_search_at: updated.last_search_at,
        last_search_added: updated.last_search_added,
        last_search_found: updated.last_search_found,
      });
      if (data.auto_search && data.search_started) {
        flash({
          ok: true,
          text: "Recherche auto ON — première recherche LinkedIn en cours…",
        });
      } else {
        flash({
          ok: true,
          text: data.auto_search
            ? "Recherche auto ON — prochaines passes à 08h00 et 20h00"
            : "Recherche auto OFF",
        });
      }
    } catch (err) {
      onUpdate({ ...segment, auto_search: next ? 0 : 1 }); // revert
      flash({ ok: false, text: err.message || "Impossible d'activer la recherche auto" });
    } finally {
      setTogglingSearch(false);
    }
  };

  const msgCount = Math.max(1, Math.min(3, Number(segment.messages_per_contact) || 1));

  const loadContacts = async () => {
    try {
      setLoadingContacts(true);
      const data = await api.get(`/api/segments/${segment.id}/contacts?limit=30`);
      const list = data.contacts || [];
      setContacts(list);
      setSearches(data.searches || []);
      setDraftById((prev) => {
        const next = { ...prev };
        for (const c of list) {
          if (c.draft_message && next[c.id] === undefined) {
            next[c.id] = parseDrafts(c.draft_message);
          }
        }
        return next;
      });
    } catch {
      setContacts([]);
      setSearches([]);
    } finally {
      setLoadingContacts(false);
    }
  };

  const handleToggleContacts = async () => {
    const next = !showContacts;
    setShowContacts(next);
    if (next) await loadContacts();
    else {
      setExpandedId(null);
    }
  };

  const ensureDraft = async (contactId, { force = false } = {}) => {
    setBusy(contactId, force ? "regen" : "preview");
    try {
      const qs = force ? "?force=1" : "";
      const data = await api.post(
        `/api/segments/${segment.id}/contacts/${contactId}/preview-dm${qs}`,
        {}
      );
      const drafts = parseDrafts(data.draft_messages || data.draft_message);
      setDraftById((prev) => ({ ...prev, [contactId]: drafts }));
      setContacts((prev) =>
        prev.map((c) =>
          c.id === contactId
            ? { ...c, draft_message: data.draft_message || drafts[0] || "" }
            : c
        )
      );
      return drafts;
    } catch (err) {
      flash({ ok: false, text: err.message || "Génération du brouillon impossible" });
      throw err;
    } finally {
      setBusy(contactId, null);
    }
  };

  const handleExpandContact = async (c) => {
    if (!isDmMode || c.status !== "pending") return;
    if (expandedId === c.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(c.id);
    const existing = draftById[c.id] ?? parseDrafts(c.draft_message);
    if (existing.length >= msgCount) {
      setDraftById((prev) => ({ ...prev, [c.id]: existing }));
      return;
    }
    if (existing.length) {
      setDraftById((prev) => ({ ...prev, [c.id]: existing }));
    }
    try {
      await ensureDraft(c.id, { force: false });
    } catch {
      /* flash already set */
    }
  };

  const persistDraft = async (contactId, messages) => {
    const cleaned = (messages || []).map((m) => String(m || "").trim()).filter(Boolean);
    if (!cleaned.length) return;
    try {
      const data = await api.patch(
        `/api/segments/${segment.id}/contacts/${contactId}/draft`,
        { messages: cleaned }
      );
      const drafts = parseDrafts(data.draft_messages || data.draft_message || cleaned);
      setDraftById((prev) => ({ ...prev, [contactId]: drafts }));
      setContacts((prev) =>
        prev.map((c) =>
          c.id === contactId ? { ...c, draft_message: data.draft_message || drafts[0] } : c
        )
      );
    } catch (err) {
      flash({ ok: false, text: err.message || "Sauvegarde du brouillon impossible" });
    }
  };

  const handleDraftChange = (contactId, index, value) => {
    setDraftById((prev) => {
      const cur = [...(prev[contactId] || [])];
      while (cur.length < msgCount) cur.push("");
      cur[index] = value;
      if (draftTimers.current[contactId]) {
        clearTimeout(draftTimers.current[contactId]);
      }
      draftTimers.current[contactId] = setTimeout(() => {
        persistDraft(contactId, cur);
      }, 600);
      return { ...prev, [contactId]: cur };
    });
  };

  const handleDraftBlur = (contactId) => {
    if (draftTimers.current[contactId]) {
      clearTimeout(draftTimers.current[contactId]);
      draftTimers.current[contactId] = null;
    }
    const msgs = draftById[contactId];
    if (msgs?.some((m) => String(m || "").trim())) {
      persistDraft(contactId, msgs);
    }
  };

  const handleSendDraft = async (contactId) => {
    const messages = (draftById[contactId] || [])
      .map((m) => String(m || "").trim())
      .filter(Boolean);
    if (!messages.length) {
      flash({ ok: false, text: "Le message est vide" });
      return;
    }
    const contactName =
      contacts.find((c) => c.id === contactId)?.name || "ce contact";
    setBusy(contactId, "send");
    try {
      if (draftTimers.current[contactId]) {
        clearTimeout(draftTimers.current[contactId]);
        draftTimers.current[contactId] = null;
      }
      const data = await api.post(
        `/api/segments/${segment.id}/contacts/${contactId}/send`,
        { messages }
      );
      const sentText = data.message_sent || messages.join(" | ");
      setContacts((prev) =>
        prev.map((c) =>
          c.id === contactId
            ? {
                ...c,
                status: "contacted",
                draft_message: "",
                last_message_sent: sentText,
                last_contacted_at: new Date().toISOString(),
              }
            : c
        )
      );
      setExpandedId(null);
      setDraftById((prev) => {
        const next = { ...prev };
        delete next[contactId];
        return next;
      });
      const n = (data.messages || messages).length;
      flash({
        ok: true,
        text: data.already_sent
          ? `Déjà envoyé à ${data.contact_name || contactName}`
          : `${n} message${n > 1 ? "s" : ""} envoyé${n > 1 ? "s" : ""} à ${
              data.contact_name || contactName
            }`,
        ms: 8000,
      });
      onUpdate({
        ...segment,
        pending_count: Math.max(0, (segment.pending_count || 1) - 1),
      });
    } catch (err) {
      flash({ ok: false, text: err.message || "Envoi impossible" });
      await loadContacts();
    } finally {
      setBusy(contactId, null);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Supprimer ce segment ? Cette action est irréversible.")) return;
    try {
      setDeleting(true);
      await api.delete(`/api/segments/${segment.id}`);
      onDelete(segment.id);
    } catch {
      setDeleting(false);
    }
  };

  const sendOn = Boolean(segment.is_active);
  const searchOn = Boolean(segment.auto_search);

  return (
    <div className="card p-6 flex flex-col">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-display font-semibold text-ink truncate tracking-tight">
            {segment.name}
          </h3>
          {segment.description && (
            <p className="text-sm text-mute mt-1 line-clamp-2">{segment.description}</p>
          )}
          {segment.target_description && (
            <div className="mt-2">
              <p className="type-label-accent mb-0.5">Cible</p>
              <p className="text-sm text-ink/70 line-clamp-3">
                {segment.target_description}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 text-sm flex-wrap">
        <span
          className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full border ${
            segment.mode === "visit"
              ? "bg-amber-50 text-amber-700 border-amber-200"
              : segment.mode === "connect"
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : segment.mode === "invitation"
              ? "bg-blue-50 text-blue-700 border-blue-200"
              : "bg-violet-50 text-violet-700 border-violet-200"
          }`}
        >
          {segment.mode === "visit"
            ? "Visite"
            : segment.mode === "connect"
            ? "Connexion"
            : segment.mode === "invitation"
            ? "Invitation"
            : "DM"}
        </span>
        <div className="flex items-center gap-1.5 text-mute">
          {segment.contacts_count || 0} contacts
          {segment.pending_count != null && (
            <span className="text-ink/40">· {segment.pending_count} en attente</span>
          )}
        </div>
        <div className="text-mute">Max {segment.max_daily_messages || 2}/jour</div>
        {(segment.hour_start != null || segment.hour_end != null) && (
          <div className="text-mute">
            {segment.hour_start ?? 8}h–{segment.hour_end ?? 18}h
          </div>
        )}
      </div>

      <SwitchRow
        title="Envoi auto"
        description={
          sendOn
            ? "ON — actions LinkedIn étalées dans la plage horaire"
            : "OFF — aucune action automatique sur ce segment"
        }
        on={sendOn}
        busy={togglingSend}
        onToggle={handleToggleSend}
      />

      <SwitchRow
        title="Recherche auto"
        description={
          searchOn
            ? "ON — recherche LinkedIn à 08h00 et 20h00 (Paris)"
            : "OFF — le segment n'est plus réalimenté automatiquement"
        }
        meta={
          segment.last_search_at || segment.last_search_found != null
            ? `Dernière recherche${
                formatWhen(segment.last_search_at) ? ` · ${formatWhen(segment.last_search_at)}` : ""
              }${
                segment.last_search_found != null
                  ? ` · ${segment.last_search_found} trouvés, ${segment.last_search_added ?? 0} ajoutés`
                  : ""
              }`
            : null
        }
        on={searchOn}
        busy={togglingSearch}
        onToggle={handleToggleAutoSearch}
      />

      {segment.ai_instructions && (
        <div className="mb-4 p-3 bg-cream rounded-lg border border-line">
          <p className="type-label-accent mb-1">Instructions IA</p>
          <p className="text-sm text-ink/60 line-clamp-3">{segment.ai_instructions}</p>
        </div>
      )}

      {feedback && (
        <div
          className={`mb-3 px-3 py-2 rounded-lg border text-sm ${
            feedback.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
              : "bg-rouge/5 border-rouge/15 text-rouge"
          }`}
        >
          {feedback.text}
        </div>
      )}

      {showContacts && (
        <div className="mb-4 border border-line rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-cream border-b border-line flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wider text-mute">
              Contacts trouvés
            </p>
            <button
              type="button"
              onClick={loadContacts}
              className="text-xs text-rouge hover:underline"
              disabled={loadingContacts}
            >
              Rafraîchir
            </button>
          </div>
          {loadingContacts ? (
            <div className="p-4 text-sm text-mute">Chargement…</div>
          ) : contacts.length === 0 ? (
            <div className="p-4 text-sm text-mute">
              Aucun contact pour l’instant. Activez la recherche auto pour alimenter le segment.
            </div>
          ) : (
            <ul className="max-h-96 overflow-y-auto divide-y divide-line">
              {contacts.map((c) => {
                const expanded = expandedId === c.id;
                const canDraft = isDmMode && c.status === "pending";
                const busy = busyById[c.id];
                return (
                  <li key={c.id} className="px-3 py-2.5">
                    <div className="flex items-start gap-2">
                      <button
                        type="button"
                        onClick={() => handleExpandContact(c)}
                        disabled={!canDraft || Boolean(busy)}
                        className={`mt-0.5 text-mute transition ${
                          canDraft
                            ? "hover:text-ink cursor-pointer"
                            : "opacity-40 cursor-default"
                        }`}
                        aria-expanded={expanded}
                        aria-label={expanded ? "Masquer le brouillon" : "Voir le brouillon"}
                        title={
                          canDraft
                            ? "Voir / éditer le message"
                            : "Brouillon disponible en mode DM (pending)"
                        }
                      >
                        <span className="inline-block w-4 text-xs">
                          {canDraft ? (expanded ? "▾" : "▸") : "·"}
                        </span>
                      </button>
                      <div className="min-w-0 flex-1">
                        <button
                          type="button"
                          onClick={() => handleExpandContact(c)}
                          disabled={!canDraft || Boolean(busy)}
                          className={`text-left w-full ${canDraft ? "cursor-pointer" : "cursor-default"}`}
                        >
                          <p className="text-sm font-medium text-ink truncate">{c.name}</p>
                          {c.headline && (
                            <p className="text-xs text-mute line-clamp-1">{c.headline}</p>
                          )}
                        </button>
                        {c.status === "error" && c.last_error && (
                          <p
                            className="text-xs text-rouge/80 mt-0.5 line-clamp-2"
                            title={c.last_error}
                          >
                            {c.last_error}
                          </p>
                        )}
                        {(c.status === "contacted" || c.status === "invited") &&
                          c.last_message_sent && (
                            <p
                              className="text-xs text-emerald-800/80 mt-1 line-clamp-2"
                              title={c.last_message_sent}
                            >
                              Envoyé : {c.last_message_sent}
                            </p>
                          )}
                        {c.status === "pending" &&
                          parseDrafts(c.draft_message).length > 0 && (
                            <p className="text-[10px] text-violet-700 mt-0.5 font-medium">
                              Brouillon prêt
                            </p>
                          )}
                        {c.status === "contacted" && c.last_contacted_at && (
                          <p className="text-[10px] text-mute mt-0.5">
                            {formatWhen(c.last_contacted_at)}
                          </p>
                        )}
                      </div>
                      <span
                        className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded border flex-shrink-0 ${statusClass(
                          c.status
                        )}`}
                      >
                        {statusLabel(c.status)}
                      </span>
                    </div>

                    {expanded && canDraft && (
                      <div className="mt-2 ml-5 p-2.5 bg-cream border border-line rounded-lg space-y-2">
                        <p className="text-[10px] uppercase tracking-wider text-mute font-semibold">
                          {msgCount > 1
                            ? `Un message découpé en ${msgCount} envois`
                            : "Message à envoyer"}
                        </p>
                        {msgCount > 1 && !(busy === "preview" || busy === "regen") && (
                          <p className="text-[11px] text-mute leading-snug">
                            Même idée, envoyée en {msgCount} bulles (comme Enter entre les
                            phrases) pour paraître plus humain.
                          </p>
                        )}
                        {busy === "preview" || busy === "regen" ? (
                          <p className="text-xs text-mute py-3">
                            {busy === "regen"
                              ? "Régénération en cours…"
                              : "Génération du message…"}
                          </p>
                        ) : (
                          Array.from({ length: msgCount }).map((_, idx) => (
                            <div key={idx} className="space-y-1">
                              {msgCount > 1 && (
                                <p className="text-[10px] font-semibold text-mute uppercase tracking-wide">
                                  Bulle {idx + 1}/{msgCount}
                                </p>
                              )}
                              <textarea
                                value={(draftById[c.id] || [])[idx] ?? ""}
                                onChange={(e) =>
                                  handleDraftChange(c.id, idx, e.target.value)
                                }
                                onBlur={() => handleDraftBlur(c.id)}
                                rows={msgCount > 1 ? 2 : 4}
                                className="w-full text-sm text-ink bg-white border border-line rounded-md px-2.5 py-2 resize-y min-h-[56px] focus:outline-none focus:ring-1 focus:ring-rouge/40"
                                placeholder={`Phrase ${idx + 1}…`}
                                disabled={Boolean(busy)}
                              />
                            </div>
                          ))
                        )}
                        <div className="flex items-center gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => ensureDraft(c.id, { force: true })}
                            disabled={Boolean(busy)}
                            className="btn-secondary text-xs py-1.5 px-2.5 disabled:opacity-50"
                          >
                            Régénérer
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSendDraft(c.id)}
                            disabled={
                              Boolean(busy) ||
                              !(draftById[c.id] || []).some((m) => String(m || "").trim())
                            }
                            className="btn-primary text-xs py-1.5 px-2.5 disabled:opacity-50"
                          >
                            {busy === "send"
                              ? "Envoi en cours…"
                              : msgCount > 1
                              ? `Envoyer (${msgCount} bulles)`
                              : "Envoyer"}
                          </button>
                          {busy === "send" && (
                            <span className="text-[10px] text-mute">
                              Envoi LinkedIn… ne rechargez pas la page
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {searches.length > 0 && (
            <div className="px-3 py-2 border-t border-line bg-cream/50">
              <p className="text-[10px] uppercase tracking-wider text-mute mb-1">
                Historique recherches
              </p>
              <ul className="space-y-1">
                {searches.slice(0, 3).map((s) => (
                  <li key={s.id} className="text-xs text-ink/60">
                    {formatWhen(s.created_at)} —{" "}
                    {s.status === "ok"
                      ? `${s.found} trouvés, ${s.added} ajoutés`
                      : s.error || "erreur"}
                    {s.keywords ? ` · « ${s.keywords} »` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <div className="mt-auto flex items-center gap-2 pt-2 flex-wrap">
        <button
          type="button"
          onClick={handleToggleContacts}
          className="btn-secondary text-sm py-2 px-3 flex items-center gap-1.5"
        >
          {showContacts ? "Masquer contacts" : "Voir contacts"}
        </button>

        <Link to={`/segments/${segment.id}/edit`} className="btn-secondary text-sm py-2 px-3">
          Modifier
        </Link>

        <button
          type="button"
          onClick={handleDelete}
          disabled={deleting}
          className="btn-danger text-sm py-2 px-3 ml-auto"
        >
          Supprimer
        </button>
      </div>
    </div>
  );
}
